import { useAtom } from "jotai";
import { useTentativeEntriesForm } from "web_myplann/Finances/Forms/useTentativeEntriesForm";
import { createTentativeIncome } from "web_myplann/Finances/Helpers/createTentativeIncome";
import { isDetailedIncome } from "web_myplann/Finances/Helpers/isDetailedIncome";
import { sumIncomeAmounts } from "web_myplann/Finances/Helpers/sumIncomeAmounts";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import type { TentativeEntriesModel } from "web_myplann/Finances/Types/TentativeEntriesModel";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { z } from "zod";

const getIncomeEntrySchema = (t: TranslateFunction) =>
	z.object({
		id: z.number().nullable(),
		name: z.string({
			required_error: t(Resources.Form.Validations.required),
		}),
		amount: z.number({
			required_error: t(Resources.Form.Validations.required),
		}),
	});

export type IncomeFormEntry = z.infer<ReturnType<typeof getIncomeEntrySchema>>;
export type IncomeFormModel = TentativeEntriesModel<IncomeFormEntry>;

export const useIncomeV2Form = () => {
	const { t } = useResource();
	const [finance, setFinance] = useAtom(financeState);

	const {
		entries,
		entryKeys,
		append,
		removeEntry,
		setValue,
		onSubmit,
		exact,
		total,
		setExact,
		setManualTotal,
		hasUnconfirmedChanges,
	} = useTentativeEntriesForm({
		entrySchema: getIncomeEntrySchema(t),
		entries: finance.income.map((income) => ({
			id: income.id,
			name: income.name ?? "",
			amount: income.amount,
		})),
		isDetailed: isDetailedIncome(finance.income),
		sum: sumIncomeAmounts,
		createPlaceholder: (id, amount) => createTentativeIncome(id, amount, t),
		save: (income) => setFinance({ ...finance, income }),
	});

	return {
		incomes: entries,
		incomeKeys: entryKeys,
		incomeAppend: append,
		removeIncome: removeEntry,
		setValue,
		onSubmit,
		exact,
		total,
		setExact,
		setManualTotal,
		hasUnconfirmedChanges,
	};
};
